/**
 * Filtered, paginated returns list for a bulk RMA action request.
 */
import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import { IOption, SelectDropdown } from '../../../../../../components/inputs/select-dropdown/select-dropdown';
import Pagination from '../../../../../../components/table/components/pagination/pagination';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../../components/fonts/font-props';
import FontBody2 from '../../../../../../components/fonts/font-body2/font-body2';
import LinearLoader from '../../../../../../components/loaders/linear-loader/linear-loader';
import bulkRmaActionRequestDetailStore from '../../stores/bulk-rma-action-request-detail-store';
import bulkRmaActionReturnsPagedListStore from '../../stores/bulk-rma-action-returns-paged-list-store';
import { RETURN_STATUS_LABELS } from '../../utils/bulk-rma-action-labels';
import ReturnsTable from '../returns-table/returns-table';

const ALL_REASONS_OPTION: IOption = { value: '', label: 'All reasons' };

const RETURN_STATUS_FILTER_OPTIONS: IOption[] = [
    { value: '', label: 'All outcomes' },
    ...Object.entries(RETURN_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const RequestReturnsSection: React.FC = observer(() => {
    const {
        list: returns,
        page,
        perPage,
        total,
        isLoading,
        error,
        returnStatusFilter,
        reasonCodeFilter,
    } = bulkRmaActionReturnsPagedListStore;
    const { detail } = bulkRmaActionRequestDetailStore;

    const returnStatusOption =
        RETURN_STATUS_FILTER_OPTIONS.find((option) => option.value === returnStatusFilter)
        ?? RETURN_STATUS_FILTER_OPTIONS[0];

    const reasonCodeOptions = useMemo((): IOption[] => {
        const reasonCounts = Object.entries(detail?.counts_by_reason ?? {});
        return [
            ALL_REASONS_OPTION,
            ...reasonCounts.map(([code, count]) => ({
                value: code,
                label: `${code} (${count})`,
            })),
        ];
    }, [detail]);

    const reasonCodeOption =
        reasonCodeOptions.find((option) => option.value === reasonCodeFilter)
        ?? ALL_REASONS_OPTION;

    return (
        <div className="bulk-rma-actions-page__detail-section">
            <h5>Returns</h5>
            <div className="bulk-rma-actions-page__returns-filters">
                <SelectDropdown
                    value={returnStatusOption}
                    options={RETURN_STATUS_FILTER_OPTIONS}
                    placeholder="Filter by outcome"
                    onChange={(val) => {
                        void bulkRmaActionReturnsPagedListStore.setReturnStatusFilter(
                            String(val?.value ?? ''),
                        );
                    }}
                />
                <SelectDropdown
                    value={reasonCodeOption}
                    options={reasonCodeOptions}
                    placeholder="Filter by reason"
                    onChange={(val) => {
                        void bulkRmaActionReturnsPagedListStore.setReasonCodeFilter(
                            String(val?.value ?? ''),
                        );
                    }}
                />
            </div>

            {error && (
                <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER} variant={FONT_VARIANT.BOLD}>
                    {error}
                </FontBody2>
            )}

            {isLoading ? (
                <LinearLoader />
            ) : returns.length === 0 ? (
                <FontBody2 color={FONT_COLOR.PRIMARY2}>No returns match the current filters.</FontBody2>
            ) : (
                <>
                    <ReturnsTable returns={returns} />
                    <Pagination
                        page={page}
                        perPage={perPage}
                        total={total}
                        totalResultsTitle="returns"
                        onSetPage={(nextPage) => {
                            void bulkRmaActionReturnsPagedListStore.loadPage(nextPage);
                        }}
                    />
                </>
            )}
        </div>
    );
});

export default RequestReturnsSection;
